/** @jsxImportSource react */
import Accordian from '@react/shared/components/Accordian';
import { Link } from '@react/system';
import { useRouter } from 'next/router';

const data = [
  {
    question: 'How does Seniorly get their pricing data?',
    answer: (
      <>
        <p>
          Seniorly uses combination of two different methods to deliver the most
          accurate pricing possible. In cases where we have confirmed pricing
          shared directly from a community owner, we will display that pricing
          instead of the Seniorly estimate. We do this to ensure we are
          delivering the most accurate set of data possible. This pricing may
          reflect room type, care type and any seasonal incentives or discounts
          a community may be offering at the time the pricing was confirmed.
        </p>
        <p>
          In addition to using real data, our team has employed feature
          engineering techniques to extract relevant features from the data and
          optimize the performance of our model. These features include
          location-specific variables, accommodation size, available amenities,
          and the level of care provided. By incorporating these features into
          our machine learning model, we can capture the nuanced factors that
          influence pricing and provide more accurate estimates.
        </p>
        <p>
          In 2022, we updated our pricing methodology to more accurately reflect
          reported prices.
        </p>
        <p>
          Learn more about our pricing estimates{' '}
          <Link
            underlined
            target='blank'
            to='/company/seniorly-estimated-pricing'
          >
            here.
          </Link>
        </p>
      </>
    ),
  },
  {
    question: 'How does Seniorly estimate the pricing?',
    answer: (
      <p>
        Here&apos;s where we get a little technical. Our pricing model utilizes
        advanced machine learning algorithms to analyze vast amounts of real
        data and extract meaningful insights. The model is built off a support
        vector regression base model to handle non-linear relationships between
        variables, which was needed to deal with complex pricing structures in
        senior living accommodations.
      </p>
    ),
  },
  {
    question: 'How often does Seniorly update their pricing?',
    answer: (
      <p>
        We understand that the senior living industry is dynamic, with pricing
        structures and market conditions constantly evolving. To keep our
        pricing model up to date and accurate, we have implemented a system for
        continuous learning and updating. This involves regularly incorporating
        real-time data from senior living communities, monitoring market trends,
        and adapting our algorithms to reflect the changing landscape. By doing
        so, we ensure that users receive the most current and accurate pricing
        information.
      </p>
    ),
  },
  {
    question: 'How accurate is Seniorly data?',
    answer: (
      <p>
        In an analysis of over 150 communities and 400 price points, our data
        shows that our estimates are 32% more accurate than the leading industry
        pricing estimate for shared rooms, and 70% more accurate for individual
        room and care types. Seniorly model is going to get better as it
        collects more data from communities and augment that with the ongoing
        move in data. The data from leading industry models is from data
        gathered in 2021. Whereas, Seniorly data is continuously being updated
        by community prices.
      </p>
    ),
  },
  {
    question: 'How many communities does this pricing data include?',
    answer: (
      <p>
        The pricing data is informed by over 40,000 price points that has been
        gathered by Seniorly over the years.
      </p>
    ),
  },
  {
    question: 'What is Seniorly?',
    answer: (
      <p>
        Seniorly is a senior living technology company enabling end-to-end
        discovery and decision for older adults and their families. Supporting
        over 5 million active searchers each year, our technology-enabled
        platform allows us to optimize occupancy economics for senior living
        investors. With over three generations of experience in senior living,
        we help providers increase occupancy the smarter way. Learn more about
        Seniorly{' '}
        <Link underlined target='blank' to='/company/about'>
          here.
        </Link>
      </p>
    ),
  },
  {
    question: 'How can Seniorly help me evaluate senior living pricing?',
    answer: (
      <>
        <p>
          Seniorly offers several ways to understand pricing as it relates to
          your care needs and your geography:
        </p>
        <p>
          Pricing comparison charts: On each community profile page, you will
          notice a section that allows you to compare the price of the community
          to average prices in nearby communities. Seniorly advisors have found
          that some families may be willing to extend their search radius to
          incorporate a lower-priced area.
        </p>
        <p>
          Cost of Care Calculator: In the same section, Seniorly has created a
          Cost of Care Calculator to help families explore pricing by geography
          and care needs.
        </p>
        <p>
          Community descriptions: In the “About” section of every community
          profile, you can find helpful information to aid you in your decision
          journey. Each description includes information about the “starting at”
          price of the community as it relates to other community pricing in
          your area.
        </p>
        <p>
          Community recommendations: When you submit your information to
          Seniorly, we will develop a personalized set of recommendations that
          takes into account your budget. These recommendations are based on a
          proprietary Seniorly Score that includes communities we know and trust
          - and that fit your care needs and budget.
        </p>
      </>
    ),
  },
];

export default function DatahubFaq() {
  const router = useRouter();
  const { query } = router;
  const { toc, state, city, roomType } = query;
  return (
    <div>
      {data.map((faq, index, faqs) => (
        <div key={faq.question}>
          <Accordian
            variant='B'
            openByDefault={index === 0}
            title={faq.question}
            className='mt-4 [&_p]:mb-2'
            eventProps={{
              location: 'seniorly data hub',
              city: city as string,
              state: state as string,
              toc: toc as string,
              roomType: roomType as string,
            }}
          >
            <div>{faq.answer}</div>
          </Accordian>
          {index < faqs.length - 1 && (
            <hr className='my-4 block border-0 border-t' />
          )}
        </div>
      ))}
    </div>
  );
}
